/* eslint-disable @typescript-eslint/no-explicit-any */
import { Select, Segmented } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useAdformDealsQuery,
  usePublisherDiscoveryDealsQuery,
  usePublisherDiscoverySitesQuery,
  usePublisherDiscoveryAppsQuery,
} from '../../modules/apiData/dataApiSlice';

type EntityType = 'all' | 'deals' | 'sites' | 'apps';

type SearchResult = {
  type: 'deal' | 'site' | 'app';
  id: string;
  displayName: string;
};

type PublisherMappingSearchProps = {
  onSelect: (entity: SearchResult) => void;
  selectedEntities: SearchResult[];
};

export const PublisherMappingSearch = ({
  onSelect,
  selectedEntities,
}: PublisherMappingSearchProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<EntityType>('all');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { data: adformDeals } = useAdformDealsQuery();
  const { data: discoveredDeals } = usePublisherDiscoveryDealsQuery(searchQuery, {
    skip: searchQuery.length < 2 || (activeFilter !== 'all' && activeFilter !== 'deals'),
  });
  const { data: discoveredSites } = usePublisherDiscoverySitesQuery(searchQuery, {
    skip: searchQuery.length < 2 || (activeFilter !== 'all' && activeFilter !== 'sites'),
  });
  const { data: discoveredApps } = usePublisherDiscoveryAppsQuery(searchQuery, {
    skip: searchQuery.length < 2 || (activeFilter !== 'all' && activeFilter !== 'apps'),
  });

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Clear search when filter changes
  useEffect(() => {
    setSearchInput('');
    setSearchQuery('');
  }, [activeFilter]);

  const handleSearch = useCallback((value: string) => {
    setSearchInput(value);
  }, []);

  // Transform and filter search results
  const searchResults = useMemo(() => {
    const results: SearchResult[] = [];

    // Clear results if user is still typing or input is too short
    if (searchInput.length < 2 || searchQuery.length < 2) return results;

    // Add deals
    if (activeFilter === 'all' || activeFilter === 'deals') {
      discoveredDeals?.forEach((dealId) => {
        const isAlreadySelected = selectedEntities.some(
          (e) => e.type === 'deal' && e.id === dealId,
        );

        if (!isAlreadySelected) {
          const deal = adformDeals?.find((d) => d.dealId === dealId);

          results.push({
            type: 'deal',
            id: dealId,
            displayName: deal?.name || dealId,
          });
        }
      });
    }

    // Add sites
    if (activeFilter === 'all' || activeFilter === 'sites') {
      discoveredSites?.forEach((domain) => {
        const isAlreadySelected = selectedEntities.some(
          (e) => e.type === 'site' && e.id === domain,
        );

        if (!isAlreadySelected) {
          results.push({
            type: 'site',
            id: domain,
            displayName: domain,
          });
        }
      });
    }

    // Add apps
    if (activeFilter === 'all' || activeFilter === 'apps') {
      discoveredApps?.forEach((appId) => {
        const isAlreadySelected = selectedEntities.some((e) => e.type === 'app' && e.id === appId);

        if (!isAlreadySelected) {
          results.push({
            type: 'app',
            id: appId,
            displayName: appId,
          });
        }
      });
    }

    return results.slice(0, 50); // Limit to 50 results
  }, [
    searchInput,
    searchQuery,
    activeFilter,
    discoveredDeals,
    discoveredSites,
    discoveredApps,
    selectedEntities,
    adformDeals,
  ]);

  const selectOptions = searchResults.map((result) => ({
    value: `${result.type}:${result.id}`,
    label: (
      <div className="flex items-center gap-2">
        <span className="text-xs px-2 py-0.5 rounded bg-primary-gray-100 text-primary-gray-600">
          {result.type}
        </span>
        <span>{result.displayName}</span>
      </div>
    ),
  }));

  const handleSelect = (value: string | null) => {
    if (!value) return;

    const [type, ...idParts] = value.split(':');
    const id = idParts.join(':');
    const result = searchResults.find((r) => r.type === type && r.id === id);

    if (result) {
      onSelect(result);
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full">
      <div className="flex items-center gap-3">
        <Segmented
          options={[
            { label: 'All', value: 'all' },
            { label: 'Deals', value: 'deals' },
            { label: 'Sites', value: 'sites' },
            { label: 'Apps', value: 'apps' },
          ]}
          value={activeFilter}
          onChange={(value) => setActiveFilter(value as EntityType)}
        />
      </div>
      <Select
        showSearch
        placeholder="Search for deals, sites, or apps (min 2 characters)..."
        className="w-full"
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        options={selectOptions}
        notFoundContent={
          searchInput.length < 2 ? (
            <div className="text-center py-4 text-primary-gray-500">
              Type at least 2 characters to search
            </div>
          ) : (
            <div className="text-center py-4 text-primary-gray-500">No results found</div>
          )
        }
        value={null}
      />
    </div>
  );
};
